package com.kanati.admin.features.loyalty

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.SelectedSchoolContext
import com.kanati.shared.loyalty.AdjustLoyaltyPointsRequest
import com.kanati.shared.loyalty.CreateLoyaltyAccountRequest
import com.kanati.shared.loyalty.CreateLoyaltyRewardRequest
import com.kanati.shared.loyalty.LoyaltyAccountDto
import com.kanati.shared.loyalty.LoyaltyPointsLedgerEntryDto
import com.kanati.shared.loyalty.LoyaltyRedemptionRequest
import com.kanati.shared.loyalty.LoyaltyRewardDto
import com.kanati.shared.loyalty.UpdateLoyaltyRewardRequest
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Composable
fun LoyaltyScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    school: SelectedSchoolContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
    canEdit: Boolean,
) {
    val scope = rememberCoroutineScope()
    var accounts by remember { mutableStateOf<List<LoyaltyAccountDto>>(emptyList()) }
    var rewards by remember { mutableStateOf<List<LoyaltyRewardDto>>(emptyList()) }
    val ledgersByAccount = remember { mutableStateMapOf<String, List<LoyaltyPointsLedgerEntryDto>>() }
    var loadError by remember { mutableStateOf<String?>(null) }
    var isRefreshing by remember { mutableStateOf(false) }

    var rewardName by remember { mutableStateOf("") }
    var rewardDescription by remember { mutableStateOf("") }
    var rewardPointsCost by remember { mutableStateOf("") }
    var rewardFixedDiscountAmount by remember { mutableStateOf("") }
    var rewardActive by remember { mutableStateOf(true) }
    var rewardMessage by remember { mutableStateOf<String?>(null) }
    var rewardError by remember { mutableStateOf<String?>(null) }
    var isCreatingReward by remember { mutableStateOf(false) }

    var accountName by remember { mutableStateOf("") }
    var accountEmail by remember { mutableStateOf("") }
    var accountPhone by remember { mutableStateOf("") }
    var accountMessage by remember { mutableStateOf<String?>(null) }
    var accountError by remember { mutableStateOf<String?>(null) }
    var isCreatingAccount by remember { mutableStateOf(false) }

    suspend fun refresh(
        selectedDistrict: SelectedDistrictContext,
        selectedSchool: SelectedSchoolContext,
    ) {
        isRefreshing = true
        loadError = null
        try {
            rewards = apiClient.getLoyaltyRewards(selectedDistrict.id, selectedSchool.id)
            accounts = apiClient.getLoyaltyAccounts(selectedDistrict.id, selectedSchool.id)
            ledgersByAccount.clear()
        } catch (exception: Exception) {
            loadError = exception.message ?: "Unable to load loyalty configuration."
        } finally {
            isRefreshing = false
        }
    }

    suspend fun refreshLedger(
        selectedDistrict: SelectedDistrictContext,
        selectedSchool: SelectedSchoolContext,
        accountId: String,
    ) {
        ledgersByAccount[accountId] = apiClient.getLoyaltyLedger(selectedDistrict.id, selectedSchool.id, accountId)
    }

    LaunchedEffect(district?.id, school?.id) {
        if (district != null && school != null) {
            refresh(district, school)
        } else {
            accounts = emptyList()
            rewards = emptyList()
            ledgersByAccount.clear()
        }
    }

    fun submitCreateReward() {
        val selectedDistrict = district ?: return
        val selectedSchool = school ?: return
        val pointsCost = rewardPointsCost.trim().toIntOrNull()
        val fixedDiscountAmount = rewardFixedDiscountAmount.trim().toLongOrNull()
        if (rewardName.isBlank() || pointsCost == null || fixedDiscountAmount == null) return

        scope.launch {
            isCreatingReward = true
            rewardError = null
            rewardMessage = null
            try {
                val created = apiClient.createLoyaltyReward(
                    CreateLoyaltyRewardRequest(
                        districtId = selectedDistrict.id,
                        schoolId = selectedSchool.id,
                        name = rewardName,
                        description = rewardDescription.ifBlank { null },
                        pointsCost = pointsCost,
                        fixedDiscountAmount = fixedDiscountAmount,
                        active = rewardActive,
                    )
                )
                rewards = (rewards + created).sortedBy { it.name.lowercase() }
                rewardName = ""
                rewardDescription = ""
                rewardPointsCost = ""
                rewardFixedDiscountAmount = ""
                rewardActive = true
                rewardMessage = "Reward '${created.name}' saved."
            } catch (exception: Exception) {
                rewardError = exception.message ?: "Unable to create reward."
            } finally {
                isCreatingReward = false
            }
        }
    }

    fun submitCreateAccount() {
        val selectedDistrict = district ?: return
        val selectedSchool = school ?: return
        if (accountName.isBlank()) return

        scope.launch {
            isCreatingAccount = true
            accountError = null
            accountMessage = null
            try {
                val created = apiClient.createLoyaltyAccount(
                    CreateLoyaltyAccountRequest(
                        districtId = selectedDistrict.id,
                        schoolId = selectedSchool.id,
                        customerName = accountName,
                        email = accountEmail.ifBlank { null },
                        phone = accountPhone.ifBlank { null },
                    )
                )
                accounts = (accounts + created).sortedBy { it.customerName.lowercase() }
                accountName = ""
                accountEmail = ""
                accountPhone = ""
                accountMessage = "Account '${created.customerName}' created."
            } catch (exception: Exception) {
                accountError = exception.message ?: "Unable to create loyalty account."
            } finally {
                isCreatingAccount = false
            }
        }
    }

    if (district == null || school == null || districtLoading || districtLoadError != null) {
        Div({ style { padding(24.px) } }) {
            P({ style { property("color", theme.onSurfaceVariant) } }) {
                Text(
                    when {
                        districtLoading -> "Loading district context..."
                        districtLoadError != null -> "District context could not be loaded."
                        district == null -> "Select a district to manage loyalty."
                        else -> "Select a school to manage loyalty."
                    }
                )
            }
        }
        return
    }

    Div({
        style {
            padding(24.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(24.px)
        }
    }) {
        loadError?.let {
            StatusBanner(
                message = it,
                tone = BannerTone.Error,
                theme = theme,
            )
        }
        if (!canEdit) {
            StatusBanner(
                message = "Read-only access. Loyalty changes require edit permission for this school.",
                tone = BannerTone.Info,
                theme = theme,
            )
        }

        SectionCard(
            title = "Rewards",
            description = "Define school-scoped fixed order discount rewards for ${school.name}. POS and backend apply these discounts during concessions checkout. Campaigns, free-item rewards, and advanced loyalty are not available for launch.",
            theme = theme,
        ) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                rewardError?.let { StatusBanner(message = it, tone = BannerTone.Error, theme = theme) }
                rewardMessage?.let { StatusBanner(message = it, tone = BannerTone.Success, theme = theme) }

                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Reward Name",
                        value = rewardName,
                        onValueChange = { rewardName = it },
                        placeholder = "$2 Off Order",
                    )
                    TextField(
                        theme = theme,
                        label = "Points Cost",
                        value = rewardPointsCost,
                        onValueChange = { rewardPointsCost = it },
                        placeholder = "100",
                    )
                    TextField(
                        theme = theme,
                        label = "Fixed Discount (cents)",
                        value = rewardFixedDiscountAmount,
                        onValueChange = { rewardFixedDiscountAmount = it },
                        placeholder = "200",
                    )
                }
                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                    Text("Enter the discount amount applied to the order when this reward is redeemed. Fixed discount amount must be greater than zero.")
                }
                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Description",
                        value = rewardDescription,
                        onValueChange = { rewardDescription = it },
                        placeholder = "Redeem points for a fixed order discount.",
                    )
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        property("flex-wrap", "wrap")
                    }
                }) {
                    ToggleChip(
                        label = if (rewardActive) "Reward Active" else "Reward Inactive",
                        isSelected = rewardActive,
                        onClick = { rewardActive = !rewardActive },
                        theme = theme,
                    )
                    ActionButton(
                        label = if (isCreatingReward) "Saving..." else "Create Reward",
                        onClick = { submitCreateReward() },
                        isDisabled = isCreatingReward ||
                            rewardName.isBlank() ||
                            rewardPointsCost.trim().toIntOrNull() == null ||
                            rewardFixedDiscountAmount.trim().toLongOrNull() == null ||
                            !canEdit,
                        theme = theme,
                    )
                }

                if (rewards.isEmpty() && !isRefreshing) {
                    P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                        Text("No rewards configured yet.")
                    }
                } else {
                    rewards.forEach { reward ->
                        RewardEditorCard(
                            reward = reward,
                            theme = theme,
                            onSave = { request ->
                                val updated = apiClient.updateLoyaltyReward(reward.id, request)
                                rewards = rewards.map { existing ->
                                    if (existing.id == updated.id) updated else existing
                                }.sortedBy { it.name.lowercase() }
                            },
                        )
                    }
                }
            }
        }

        SectionCard(
            title = "Accounts",
            description = "Create and view loyalty accounts, adjust balances for MVP operations, and redeem fixed order discount rewards. Member codes are generated automatically by the backend.",
            theme = theme,
        ) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                accountError?.let { StatusBanner(message = it, tone = BannerTone.Error, theme = theme) }
                accountMessage?.let { StatusBanner(message = it, tone = BannerTone.Success, theme = theme) }

                FieldRow {
                    TextField(
                        theme = theme,
                        label = "Customer Name",
                        value = accountName,
                        onValueChange = { accountName = it },
                        placeholder = "Jordan Taylor",
                    )
                    TextField(
                        theme = theme,
                        label = "Email",
                        value = accountEmail,
                        onValueChange = { accountEmail = it },
                        placeholder = "jordan@example.com",
                    )
                    TextField(
                        theme = theme,
                        label = "Phone",
                        value = accountPhone,
                        onValueChange = { accountPhone = it },
                        placeholder = "5558675309",
                    )
                }
                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                    Text("Member code is generated automatically when the account is created.")
                }

                ActionButton(
                    label = if (isCreatingAccount) "Creating..." else "Create Account",
                    onClick = { submitCreateAccount() },
                    isDisabled = isCreatingAccount || accountName.isBlank() || !canEdit,
                    theme = theme,
                )

                if (accounts.isEmpty() && !isRefreshing) {
                    P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                        Text("No loyalty accounts yet.")
                    }
                } else {
                    accounts.forEach { account ->
                        LoyaltyAccountCard(
                            account = account,
                            rewards = rewards,
                            ledgerEntries = ledgersByAccount[account.id],
                            theme = theme,
                            onRefreshLedger = {
                                scope.launch {
                                    refreshLedger(district, school, account.id)
                                }
                            },
                            onAdjustPoints = { delta, reason ->
                                scope.launch {
                                    apiClient.adjustLoyaltyPoints(
                                        accountId = account.id,
                                        request = AdjustLoyaltyPointsRequest(
                                            districtId = district.id,
                                            schoolId = school.id,
                                            pointsDelta = delta,
                                            reason = reason,
                                        ),
                                    )
                                    refresh(district, school)
                                    refreshLedger(district, school, account.id)
                                }
                            },
                            onRedeemReward = { rewardId ->
                                scope.launch {
                                    apiClient.redeemLoyaltyReward(
                                        accountId = account.id,
                                        request = LoyaltyRedemptionRequest(
                                            districtId = district.id,
                                            schoolId = school.id,
                                            rewardId = rewardId,
                                        ),
                                    )
                                    refresh(district, school)
                                    refreshLedger(district, school, account.id)
                                }
                            },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun RewardEditorCard(
    reward: LoyaltyRewardDto,
    theme: MaterialThemePalette,
    onSave: suspend (UpdateLoyaltyRewardRequest) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var draftName by remember(reward.id, reward.updatedAt) { mutableStateOf(reward.name) }
    var draftDescription by remember(reward.id, reward.updatedAt) { mutableStateOf(reward.description.orEmpty()) }
    var draftPointsCost by remember(reward.id, reward.updatedAt) { mutableStateOf(reward.pointsCost.toString()) }
    var draftFixedDiscountAmount by remember(reward.id, reward.updatedAt) { mutableStateOf(reward.fixedDiscountAmount.toString()) }
    var draftActive by remember(reward.id, reward.updatedAt) { mutableStateOf(reward.active) }
    var saveError by remember(reward.id) { mutableStateOf<String?>(null) }
    var saveSuccess by remember(reward.id) { mutableStateOf<String?>(null) }
    var isSaving by remember(reward.id) { mutableStateOf(false) }

    Div({
        style {
            padding(18.px)
            property("border", "1px solid ${theme.outlineVariant}")
            borderRadius(24.px)
            property("background-color", theme.surface)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(14.px)
        }
    }) {
        H3({ style { margin(0.px); property("color", theme.onSurface) } }) {
            Text(reward.name)
        }
        saveError?.let { StatusBanner(message = it, tone = BannerTone.Error, theme = theme) }
        saveSuccess?.let { StatusBanner(message = it, tone = BannerTone.Success, theme = theme) }

        FieldRow {
            TextField(
                theme = theme,
                label = "Reward Name",
                value = draftName,
                onValueChange = { draftName = it },
            )
            TextField(
                theme = theme,
                label = "Points Cost",
                value = draftPointsCost,
                onValueChange = { draftPointsCost = it },
            )
            TextField(
                theme = theme,
                label = "Fixed Discount (cents)",
                value = draftFixedDiscountAmount,
                onValueChange = { draftFixedDiscountAmount = it },
            )
        }
        FieldRow {
            TextField(
                theme = theme,
                label = "Description",
                value = draftDescription,
                onValueChange = { draftDescription = it },
            )
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                property("flex-wrap", "wrap")
            }
        }) {
            ToggleChip(
                label = if (draftActive) "Active" else "Inactive",
                isSelected = draftActive,
                onClick = { draftActive = !draftActive },
                theme = theme,
            )
            ActionButton(
                label = if (isSaving) "Saving..." else "Save Reward",
                onClick = {
                    val pointsCost = draftPointsCost.trim().toIntOrNull() ?: return@ActionButton
                    val fixedDiscountAmount = draftFixedDiscountAmount.trim().toLongOrNull() ?: return@ActionButton
                    scope.launch {
                        isSaving = true
                        saveError = null
                        saveSuccess = null
                        try {
                            onSave(
                                UpdateLoyaltyRewardRequest(
                                    districtId = reward.districtId,
                                    schoolId = reward.schoolId,
                                    name = draftName,
                                    description = draftDescription.ifBlank { null },
                                    pointsCost = pointsCost,
                                    fixedDiscountAmount = fixedDiscountAmount,
                                    active = draftActive,
                                )
                            )
                            saveSuccess = "Reward updated."
                        } catch (exception: Exception) {
                            saveError = exception.message ?: "Unable to update reward."
                        } finally {
                            isSaving = false
                        }
                    }
                },
                isDisabled = isSaving ||
                    draftName.isBlank() ||
                    draftPointsCost.trim().toIntOrNull() == null ||
                    draftFixedDiscountAmount.trim().toLongOrNull() == null,
                theme = theme,
            )
        }
    }
}

@Composable
private fun LoyaltyAccountCard(
    account: LoyaltyAccountDto,
    rewards: List<LoyaltyRewardDto>,
    ledgerEntries: List<LoyaltyPointsLedgerEntryDto>?,
    theme: MaterialThemePalette,
    onRefreshLedger: () -> Unit,
    onAdjustPoints: suspend (Int, String) -> Unit,
    onRedeemReward: suspend (String) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var pointsAmount by remember(account.id, account.updatedAt) { mutableStateOf("") }
    var reason by remember(account.id) { mutableStateOf("") }
    var selectedRewardId by remember(account.id, rewards.map { it.id to it.active }.toString()) {
        mutableStateOf(rewards.firstOrNull { it.active }?.id)
    }
    var actionError by remember(account.id) { mutableStateOf<String?>(null) }
    var actionMessage by remember(account.id) { mutableStateOf<String?>(null) }
    var isSubmitting by remember(account.id) { mutableStateOf(false) }

    Div({
        style {
            padding(20.px)
            property("border", "1px solid ${theme.outlineVariant}")
            borderRadius(24.px)
            property("background-color", theme.surface)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(16.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                property("justify-content", "space-between")
                property("align-items", "center")
                property("flex-wrap", "wrap")
                gap(10.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(6.px)
                }
            }) {
                H3({ style { margin(0.px); property("color", theme.onSurface) } }) {
                    Text(account.customerName)
                }
                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                    Text(
                        listOfNotNull(
                            account.email,
                            account.phone,
                            account.memberCode?.let { "Code $it" },
                        ).joinToString(" • ").ifBlank { "No lookup details on file" }
                    )
                }
            }

            Div({
                style {
                    padding(10.px, 14.px)
                    borderRadius(18.px)
                    property("background-color", theme.surfaceContainer)
                    property("border", "1px solid ${theme.outlineVariant}")
                }
            }) {
                Text("${account.pointsBalance} points")
            }
        }

        actionError?.let { StatusBanner(message = it, tone = BannerTone.Error, theme = theme) }
        actionMessage?.let { StatusBanner(message = it, tone = BannerTone.Success, theme = theme) }

        FieldRow {
            TextField(
                theme = theme,
                label = "Adjust Points",
                value = pointsAmount,
                onValueChange = { pointsAmount = it },
                placeholder = "25",
            )
            TextField(
                theme = theme,
                label = "Reason",
                value = reason,
                onValueChange = { reason = it },
                placeholder = "Welcome bonus",
            )
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                property("flex-wrap", "wrap")
            }
        }) {
            ActionButton(
                label = if (isSubmitting) "Applying..." else "Award Points",
                onClick = {
                    val amount = pointsAmount.trim().toIntOrNull()?.takeIf { it > 0 } ?: return@ActionButton
                    if (reason.isBlank()) return@ActionButton
                    scope.launch {
                        isSubmitting = true
                        actionError = null
                        actionMessage = null
                        try {
                            onAdjustPoints(amount, reason)
                            pointsAmount = ""
                            reason = ""
                            actionMessage = "Points updated."
                        } catch (exception: Exception) {
                            actionError = exception.message ?: "Unable to adjust points."
                        } finally {
                            isSubmitting = false
                        }
                    }
                },
                isDisabled = isSubmitting || pointsAmount.trim().toIntOrNull()?.let { it <= 0 } != false || reason.isBlank(),
                theme = theme,
            )
            ActionButton(
                label = if (isSubmitting) "Applying..." else "Deduct Points",
                onClick = {
                    val amount = pointsAmount.trim().toIntOrNull()?.takeIf { it > 0 } ?: return@ActionButton
                    if (reason.isBlank()) return@ActionButton
                    scope.launch {
                        isSubmitting = true
                        actionError = null
                        actionMessage = null
                        try {
                            onAdjustPoints(-amount, reason)
                            pointsAmount = ""
                            reason = ""
                            actionMessage = "Points updated."
                        } catch (exception: Exception) {
                            actionError = exception.message ?: "Unable to adjust points."
                        } finally {
                            isSubmitting = false
                        }
                    }
                },
                isDisabled = isSubmitting || pointsAmount.trim().toIntOrNull()?.let { it <= 0 } != false || reason.isBlank(),
                theme = theme,
            )
            ActionButton(
                label = "Load Ledger",
                onClick = {
                    scope.launch {
                        actionError = null
                        runCatching { onRefreshLedger() }
                            .onFailure { actionError = it.message ?: "Unable to load ledger." }
                    }
                },
                isPrimary = false,
                theme = theme,
                isDisabled = isSubmitting,
            )
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                    Text("Redeem fixed order discount")
                }
            if (rewards.none { it.active }) {
                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                    Text("Create at least one active reward before redeeming.")
                }
            } else {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        property("flex-wrap", "wrap")
                    }
                }) {
                    rewards.filter { it.active }.forEach { reward ->
                        ToggleChip(
                            label = "${reward.name} (${reward.pointsCost})",
                            isSelected = selectedRewardId == reward.id,
                            onClick = { selectedRewardId = reward.id },
                            theme = theme,
                        )
                    }
                }
                ActionButton(
                    label = "Redeem Selected Reward",
                    onClick = {
                        val rewardId = selectedRewardId ?: return@ActionButton
                        scope.launch {
                            isSubmitting = true
                            actionError = null
                            actionMessage = null
                            try {
                                onRedeemReward(rewardId)
                                actionMessage = "Reward redeemed."
                            } catch (exception: Exception) {
                                actionError = exception.message ?: "Unable to redeem reward."
                            } finally {
                                isSubmitting = false
                            }
                        }
                    },
                    isDisabled = isSubmitting || selectedRewardId == null,
                    theme = theme,
                )
            }
        }

        ledgerEntries?.let { entries ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                }
            }) {
                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                    Text("Recent ledger entries")
                }
                if (entries.isEmpty()) {
                    P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                        Text("No ledger entries yet.")
                    }
                } else {
                    entries.take(6).forEach { entry ->
                        Div({
                            style {
                                padding(12.px, 14.px)
                                borderRadius(18.px)
                                property("background-color", theme.surfaceContainerHighest)
                                property("border", "1px solid ${theme.outlineVariant}")
                                display(DisplayStyle.Flex)
                                property("justify-content", "space-between")
                                property("gap", "12px")
                                property("flex-wrap", "wrap")
                            }
                        }) {
                            Text(entry.reason)
                            Text("${if (entry.pointsDelta > 0) "+" else ""}${entry.pointsDelta}")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ToggleChip(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    theme: MaterialThemePalette,
) {
    Button(attrs = {
        onClick { onClick() }
        style {
            padding(8.px, 12.px)
            borderRadius(999.px)
            property("border", "1px solid ${if (isSelected) theme.primary else theme.outlineVariant}")
            property("background-color", if (isSelected) theme.surfaceTint else theme.surface)
            property("color", if (isSelected) theme.onSurface else theme.onSurfaceVariant)
            property("cursor", "pointer")
        }
    }) {
        Text(label)
    }
}
